/** AI Product Mapping — shared constants. */

// Virtualized grid geometry
export const PM_ROW_H = 44;
export const PM_OVERSCAN = 8;

// Real run: flush buffered match/nomatch frames into the grid once this many
// have accumulated (the stream thunk also flushes on each `progress` frame and
// `done`), so the virtualized grid stays responsive at ~10k rows without a
// dispatch/row.
export const MATCH_FLUSH_SIZE = 300;

// Time-based flush companion to MATCH_FLUSH_SIZE: server `progress` frames only
// arrive at batch boundaries (hundreds of rows), so without a timer the grid and
// tallies would sit still between them. ~4 dispatches/second keeps the UI moving
// while staying far below a dispatch-per-row.
export const MATCH_FLUSH_INTERVAL_MS = 250;

// Decision reason on rows a constrained re-match committed ("this group IS product X" —
// the reviewer chose the product, the engine picked its best variant). Single source for
// the wire string: mappers, policy, chips, and hover copy all key off it.
export const REMATCH_REASON = 'constrained_match';

// Match source on those rows' committed candidates (vs 'hybrid'/'lexical' recall).
export const REMATCH_SOURCE = 'constrained';

/** One logical field the matcher recognizes (sku-mapping LogicalField enum). */
export interface LogicalField {
  key: string;
  label: string;
}

// Logical fields the matcher recognizes. Drives the interactive column-review
// dropdown and binding↔display re-normalization.
export const LOGICAL_FIELDS: LogicalField[] = [
  { key: 'sku', label: 'SKU' },
  { key: 'title', label: 'Product Title' },
  { key: 'description', label: 'Description' },
  { key: 'brand', label: 'Brand' },
  { key: 'model', label: 'Model' },
  { key: 'category', label: 'Category' },
  { key: 'color', label: 'Color' },
  { key: 'size', label: 'Size' },
  { key: 'material', label: 'Material' },
  // The vendor's product-grouping id (PromoStandards productId, parent/style id): rows
  // sharing one value are one product. Feeds the engine's product-grain type consensus.
  { key: 'productGroup', label: 'Product Group' },
];

// Fields the run cannot proceed without — the review step blocks until mapped.
export const REQUIRED_LOGICAL_FIELDS = ['sku', 'title'];

// Fields the matcher concatenates across every bound column, so mapping several
// columns to them is valid and useful (e.g. Title + Subtitle, Category + Sub
// Category). Every other logical field is single-valued — the matcher uses only
// the first bound column — so the column review enforces a 1:1 mapping for them.
export const MULTI_VALUED_FIELDS = ['title', 'description', 'category'];

// Event log cap (the run slice trims beyond this)
export const EVENTS_CAP = 500;

export const ROW_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  HI: 'hi',
  LOW: 'low',
  MULTI: 'multi',
  NOMATCH: 'nomatch',
  ERROR: 'error',
} as const;

export type RowStatus = (typeof ROW_STATUS)[keyof typeof ROW_STATUS];

export const CATALOG_TYPES = {
  MERCHANT: 'merchant',
  VENDOR: 'vendor',
} as const;

/** The preparation stage whose `total` is the run's PRODUCT-GROUP count. Every other stage
 *  reports its own unrelated total (rows scanned, classification queries, groups escalated to
 *  the judge), so anything wanting the group total has to key off this stage specifically. */
export const STAGE_MATCHING_PRODUCTS = 'matching_products';
// Stage 3, the AI judge over escalated groups — the one stage that OVERLAPS row processing.
export const STAGE_JUDGING_PRODUCTS = 'judging_products';
/** The judge tail: writing high-confidence AI decisions onto their groups' member rows.
 *  Its total is ROWS to rewrite, so it never speaks about groups. */
export const STAGE_APPLYING_DECISIONS = 'applying_decisions';

/** Status-line precedence between preparation stages. The AI type classification runs
 *  CONCURRENTLY with the group funnel server-side, so its heartbeats interleave with
 *  matching_products frames — a later pipeline stage must keep the status line once it has
 *  spoken (earlier stages keep ticking on their own rail lines). Unknown stages rank 0. */
export const PREP_STAGE_RANK: Record<string, number> = {
  analyzing_types: 1,
  classifying_types: 2,
  [STAGE_MATCHING_PRODUCTS]: 3,
  [STAGE_JUDGING_PRODUCTS]: 4,
  // Runs strictly after the judge settles, so it must hold the line once it speaks.
  [STAGE_APPLYING_DECISIONS]: 5,
};

/** Run pipeline modes (run-level choice, persisted on the run; re-run inherits it).
 *  Product-first is the default surface; row-first keeps today's flat pass byte-identical. */
export const RUN_MODES = {
  PRODUCT_FIRST: 'product_first',
  ROW_FIRST: 'row_first',
} as const;

/** A grid-toolbar filter chip. `dot` is the status indicator color; `group`
 *  drives the vertical separators between chip groups. */
export interface GridFilter {
  key: string;
  label: string;
  dot?: string;
  /** Renders the purple sparkles icon instead of a dot — the AI-review chips. */
  sparkle?: boolean;
  group: number;
}

export const GRID_FILTERS: GridFilter[] = [
  { key: 'all', label: 'All', group: 0 },
  { key: 'matched', label: 'Matched', dot: '#1DB148', group: 1 },
  { key: 'hi', label: 'High conf.', dot: '#1DB148', group: 1 },
  { key: 'low', label: 'Low conf.', dot: '#F1C21B', group: 1 },
  { key: 'multi', label: 'Ambiguous', dot: '#8352EC', group: 1 },
  { key: 'nomatch', label: 'No match', dot: '#DA1E28', group: 1 },
  { key: 'error', label: 'Errors', dot: '#DA1E28', group: 1 },
  { key: 'needs_review', label: 'Needs review', dot: '#F1C21B', group: 2 },
  // Every row with a recorded decision: confirmed/edited matches AND decided no-matches
  // (marked, group-rejected, type-gated) — "Confirmed / edited" would claim rows nobody
  // confirmed or edited.
  { key: 'confirmed', label: 'Reviewed', dot: '#1DB148', group: 2 },
  // The on-demand AI review's advisory verdicts, group-grain and product-first only —
  // their counts stay absent elsewhere, which hides the chips.
  { key: 'ai_suggested', label: 'AI suggested', sparkle: true, group: 3 },
  { key: 'ai_nomatch', label: 'AI no match', sparkle: true, group: 3 },
];

/** One segment of the completed-run result bar. `key` is the RowCounts bucket it sizes by. */
export interface ResultSegment {
  key: 'matched' | 'nomatch' | 'nomatch_open' | 'error';
  label: string;
  color: string;
}

// Completed-run result-bar segments, best → worst — the buckets partition every processed
// row exactly once ("matched" = hi + low + multi; "confirmed" is a review overlay on top
// and would double-count). One segment per strip tally (matched / awaiting review /
// no match / errors) so the bar and the numbers under it tell the same story — the
// confidence tiers stay a chip-filter concern. Errors darken the no-match red so the two
// stay distinguishable when adjacent, including for red-blind viewers.
export const RESULT_SEGMENTS: ResultSegment[] = [
  { key: 'matched', label: 'Matched', color: '#1DB148' },
  // Open no-matches are review work, not an answer — amber, never the decided red.
  { key: 'nomatch_open', label: 'Awaiting review', color: '#F1C21B' },
  { key: 'nomatch', label: 'No match', color: '#DA1E28' },
  { key: 'error', label: 'Errors', color: '#A2191F' },
];

/** Review-grid column sizing. Each column is { min(px), grow(fr) }; grow 0 → fixed at min. */
export interface ColSize {
  min: number;
  grow: number;
}

export const ROW_INDEX_WIDTH = 48;

// Leading bulk-select checkbox track — present only on editable grids (readOnly review
// grids render without it, so the templates must stay in sync via MappingGrid).
export const SELECT_COL_WIDTH = 36;

// FIXED tail columns: Type, OrderMesh match, Confidence, Status, Actions. Type is the
// engine's detected product type + catalog-coverage icon — it leads the tail because it
// describes the SOURCE row, and type problems cluster (all the pens together), so a
// scannable column beats per-row hovers. The match column is the widest + highest-grow
// track so a full variant SKU ("ThrowPillow-SpunPolyester-26x26-CoverOnly") plus a
// "+N more" tag fits on one line — the distinguishing size/variant lives at the END of
// the SKU, so it must not be truncated away (MatchCell's `truncate` is a safety net).
export const RESULT_COL_SIZES: ColSize[] = [
  { min: 110, grow: 0 },
  { min: 400, grow: 1.6 },
  { min: 120, grow: 0 },
  { min: 130, grow: 0 },
  { min: 110, grow: 0 },
];

// Per-source-column sizing by logical-field key (fallback for anything unlisted).
export const SOURCE_COL_SIZES: Record<string, ColSize> = {
  sku: { min: 150, grow: 0.8 },
  title: { min: 220, grow: 1.6 },
  description: { min: 220, grow: 1.4 },
};
export const SOURCE_COL_SIZE_DEFAULT: ColSize = { min: 150, grow: 0.8 };

// Run limits, mirrored from the sku-mapping service's MatchingRunOptions (MaxFileSizeBytes =
// 131_072_000 and MaxRowsPerRun = 50000 in its appsettings.json). The service is the authority
// and does not publish either value to the client, so these are a COPY kept in sync by hand:
// the size is a client-side pre-flight reject (a friendly message beats uploading 125 MB to be
// refused), and the row figure is upload-page copy only. Setting either lower than the service
// silently blocks files it would happily accept.
export const MAX_FILE_SIZE_MIB = 125;
export const MAX_ROWS_PER_RUN = 50_000;
